// @ts-nocheck: Legacy page using mock data. Will be rewritten to use real API services.
import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  X,
  CheckCircle,
  AlertCircle,
  FileText,
  Building2,
  Clock,
  Edit3,
  ArrowLeft,
  FileCheck,
} from 'lucide-react';
import { mockKYCSubmissions } from '@/mocks/kyc.mock';
import { KYCStatus, DocumentType } from '@apptypes/legacy';
import { formatDistanceToNow, format } from 'date-fns';
import { toast } from 'sonner';

/**
 * KYC Detail Page - matches internal-kyc-detail.html design exactly
 * Displays detailed KYC submission information with review capabilities
 */

export const KYCDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionDetails, setRejectionDetails] = useState('');
  const [internalNotes, setInternalNotes] = useState(
    'All documents appear legitimate. Business location verified via Google Maps. No red flags detected.'
  );

  // Checklist state
  const [checklist, setChecklist] = useState({
    businessRegistration: true,
    nationalId: true,
    kraPin: false,
    bankAccount: true,
    businessAddress: true,
    storePhotos: true,
    fraudCheck: true,
  });

  // Find the submission
  const submission = mockKYCSubmissions.find((s) => s.id === id);

  if (!submission) {
    return (
      <div className="p-8 text-center py-12">
        <p className="text-[var(--text-muted)]">KYC submission not found</p>
        <Link
          to="/kyc"
          className="text-[var(--text-accent)] hover:text-[var(--text-accent)] mt-4 inline-block text-[14px] font-medium"
        >
          Back to KYC Queue
        </Link>
      </div>
    );
  }

  // Get status badge classes: design system pill tokens
  const getStatusBadgeClass = (status: KYCStatus) => {
    switch (status) {
      case KYCStatus.SUBMITTED:
        return 'rounded-full bg-[var(--state-warning-bg)] text-[var(--state-warning)] text-[11px] font-semibold px-2.5 py-1';
      case KYCStatus.UNDER_REVIEW:
        return 'rounded-full bg-[var(--bg-elevated)] text-[var(--text-accent)] text-[11px] font-semibold px-2.5 py-1';
      case KYCStatus.APPROVED:
        return 'rounded-full bg-[var(--state-success-bg)] text-[var(--state-success)] text-[11px] font-semibold px-2.5 py-1';
      case KYCStatus.REJECTED:
        return 'rounded-full bg-[var(--state-danger-bg)] text-[var(--state-danger)] text-[11px] font-semibold px-2.5 py-1';
      default:
        return 'rounded-full bg-[var(--bg-base)] text-[var(--text-muted)] text-[11px] font-semibold px-2.5 py-1';
    }
  };

  // Get status text
  const getStatusText = (status: KYCStatus) => {
    switch (status) {
      case KYCStatus.SUBMITTED:
        return 'Pending Review';
      case KYCStatus.UNDER_REVIEW:
        return 'In Review';
      case KYCStatus.APPROVED:
        return 'Approved';
      case KYCStatus.REJECTED:
        return 'Rejected';
      default:
        return status;
    }
  };

  // Get avatar initials
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  // Get document type label
  const getDocumentTypeLabel = (type: DocumentType) => {
    switch (type) {
      case DocumentType.ID_DOCUMENT:
        return 'National ID Card';
      case DocumentType.BUSINESS_REGISTRATION:
        return 'Business Certificate';
      case DocumentType.TAX_CERTIFICATE:
        return 'KRA PIN Certificate';
      case DocumentType.BANK_STATEMENT:
        return 'Bank Statement';
      case DocumentType.PROOF_OF_ADDRESS:
        return 'Proof of Address';
      default:
        return type;
    }
  };

  // Get document metadata
  const getDocumentMetadata = (doc: any) => {
    switch (doc.type) {
      case DocumentType.ID_DOCUMENT:
        return 'ID: 12345678 • Front & Back';
      case DocumentType.BUSINESS_REGISTRATION:
        return `Reg: ${submission.registrationNumber}`;
      case DocumentType.TAX_CERTIFICATE:
        return `PIN: ${submission.taxId}`;
      case DocumentType.BANK_STATEMENT:
        return 'Last 3 months • Equity Bank';
      case DocumentType.PROOF_OF_ADDRESS:
        return 'Utility Bill • Dec 2025';
      default:
        return 'Document';
    }
  };

  // Handle approve
  const handleApprove = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success('KYC submission approved successfully!');
    setShowApproveModal(false);
    setTimeout(() => navigate('/kyc'), 1000);
  };

  // Handle reject
  const handleReject = (e: React.FormEvent) => {
    e.preventDefault();
    if (!rejectionReason || !rejectionDetails) {
      toast.error('Please provide rejection reason and details');
      return;
    }
    toast.error('KYC submission rejected');
    setShowRejectModal(false);
    setTimeout(() => navigate('/kyc'), 1000);
  };

  // Save notes
  const handleSaveNotes = () => {
    toast.success('Notes saved successfully');
  };

  // Toggle checklist item
  const toggleChecklistItem = (key: keyof typeof checklist) => {
    setChecklist((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Get priority (mock)
  const getPriority = () => {
    const hoursSinceSubmission =
      (Date.now() - new Date(submission.submittedAt).getTime()) / (1000 * 60 * 60);
    if (hoursSinceSubmission < 6) return 'High Priority';
    if (hoursSinceSubmission < 24) return 'Medium Priority';
    return 'Low Priority';
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)] p-8">
      {/* Back button */}
      <Link
        to="/kyc"
        className="inline-flex items-center gap-2 text-[14px] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        KYC Queue
      </Link>

      {/* Page header row */}
      <div className="flex items-start justify-between mb-8">
        <div className="flex items-center gap-4">
          {/* Flat avatar: no gradient */}
          <div className="w-14 h-14 rounded-2xl bg-[var(--bg-elevated)] flex items-center justify-center text-[var(--text-accent)] text-[20px] font-semibold flex-shrink-0">
            {getInitials(submission.businessName)}
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-1">
              KYC Review
            </p>
            <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)] leading-tight">
              {submission.businessName}
            </h1>
            <div className="flex items-center gap-3 mt-1.5">
              <span className={getStatusBadgeClass(submission.status)}>
                {getStatusText(submission.status)}
              </span>
              <span className="rounded-full bg-[var(--state-danger-bg)] text-[var(--state-danger)] text-[11px] font-semibold px-2.5 py-1 inline-flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                {getPriority()}
              </span>
              <span className="text-[14px] text-[var(--text-muted)]">
                Submitted{' '}
                {formatDistanceToNow(new Date(submission.submittedAt), { addSuffix: true })}
              </span>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 flex-shrink-0">
          <button
            onClick={() => setShowRejectModal(true)}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-5 py-2.5 text-[14px] font-medium text-[var(--state-danger)] transition-all duration-200 hover:border-[var(--state-danger)]"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
            Reject
          </button>
          <button
            onClick={() => setShowApproveModal(true)}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--gradient-to)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
          >
            <CheckCircle className="w-4 h-4" strokeWidth={1.5} />
            Approve KYC
          </button>
          <button className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-all duration-200 hover:border-[var(--border-strong)]">
            <FileCheck className="w-4 h-4" strokeWidth={1.5} />
            Request Documents
          </button>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-3 gap-6">
        {/* Main Panel */}
        <div className="col-span-2 space-y-6">
          {/* Documents Section */}
          <div className="rounded-3xl bg-white shadow-soft p-6">
            <div className="flex items-center justify-between mb-5 pb-4 border-b border-[var(--border-subtle)]">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
                <span className="text-[17px] font-semibold text-[var(--text-primary)]">
                  Submitted Documents
                </span>
              </div>
              <span className="text-[13px] font-semibold text-[var(--state-success)]">
                {submission.documents.filter((d) => d.status === 'VERIFIED').length}/
                {submission.documents.length} Complete
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {submission.documents.map((doc) => (
                <div
                  key={doc.id}
                  className="rounded-2xl border border-[var(--border-subtle)] bg-white p-4 hover:border-[var(--border-default)] transition-all cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-3">
                    {/* Document icon: flat, no gradient */}
                    <div className="w-9 h-9 rounded-xl bg-[var(--bg-elevated)] flex items-center justify-center">
                      <FileText className="w-4.5 h-4.5 text-[var(--text-accent)]" strokeWidth={1.5} />
                    </div>
                    <span
                      className={
                        doc.status === 'VERIFIED'
                          ? 'rounded-full bg-[var(--state-success-bg)] text-[var(--state-success)] text-[11px] font-semibold px-2.5 py-1 inline-flex items-center gap-1'
                          : 'rounded-full bg-[var(--state-warning-bg)] text-[var(--state-warning)] text-[11px] font-semibold px-2.5 py-1 inline-flex items-center gap-1'
                      }
                    >
                      {doc.status === 'VERIFIED' ? (
                        <CheckCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                      ) : (
                        <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                      )}
                      {doc.status === 'VERIFIED' ? 'Verified' : 'Pending'}
                    </span>
                  </div>
                  <div className="text-[13px] font-semibold text-[var(--text-primary)] mb-0.5">
                    {getDocumentTypeLabel(doc.type)}
                  </div>
                  <div className="text-[12px] text-[var(--text-muted)] mb-3">{getDocumentMetadata(doc)}</div>
                  <div className="pt-3 border-t border-[var(--border-subtle)]">
                    <div className="w-full h-20 bg-[var(--bg-base)] rounded-xl flex items-center justify-center">
                      <a
                        href="#"
                        className="text-[13px] font-medium text-[var(--text-accent)] hover:text-[var(--text-accent)]"
                        onClick={(e) => e.preventDefault()}
                      >
                        Click to view document
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Verification Checklist */}
          <div className="rounded-3xl bg-white shadow-soft p-6">
            <div className="flex items-center gap-2 mb-5 pb-4 border-b border-[var(--border-subtle)]">
              <CheckCircle className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
              <span className="text-[17px] font-semibold text-[var(--text-primary)]">
                Verification Checklist
              </span>
            </div>

            <div>
              <ChecklistItem
                checked={checklist.businessRegistration}
                onToggle={() => toggleChecklistItem('businessRegistration')}
                label="Business registration is valid and matches provided details"
                description="Certificate number verified against government registry"
              />
              <ChecklistItem
                checked={checklist.nationalId}
                onToggle={() => toggleChecklistItem('nationalId')}
                label="National ID is authentic and belongs to business owner"
                description="ID verified, photo matches, not expired"
              />
              <ChecklistItem
                checked={checklist.kraPin}
                onToggle={() => toggleChecklistItem('kraPin')}
                label="KRA PIN certificate is valid and active"
                description="PIN matches business name and is in good standing"
              />
              <ChecklistItem
                checked={checklist.bankAccount}
                onToggle={() => toggleChecklistItem('bankAccount')}
                label="Bank account is active and belongs to business"
                description="Recent transactions visible, account holder name matches"
              />
              <ChecklistItem
                checked={checklist.businessAddress}
                onToggle={() => toggleChecklistItem('businessAddress')}
                label="Business address is verifiable and operational"
                description="Utility bill recent, address matches registration"
              />
              <ChecklistItem
                checked={checklist.storePhotos}
                onToggle={() => toggleChecklistItem('storePhotos')}
                label="Store photos show legitimate business premises"
                description="Photos are clear, show actual store, match business type"
              />
              <ChecklistItem
                checked={checklist.fraudCheck}
                onToggle={() => toggleChecklistItem('fraudCheck')}
                label="No red flags in fraud database or watchlists"
                description="Business and owner checked against known fraud indicators"
              />
            </div>
          </div>
        </div>

        {/* Sidebar Panel */}
        <div className="space-y-6">
          {/* Business Information */}
          <div className="rounded-3xl bg-white shadow-soft p-6">
            <div className="flex items-center gap-2 mb-5 pb-4 border-b border-[var(--border-subtle)]">
              <Building2 className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
              <span className="text-[17px] font-semibold text-[var(--text-primary)]">Business Information</span>
            </div>

            <div className="space-y-0">
              <InfoRow label="Email" value={submission.merchantEmail} />
              <InfoRow label="Phone" value="+254 712 345 678" />
              <InfoRow label="Category" value={submission.businessType} />
              <InfoRow label="Location" value="Nairobi CBD" />
              <InfoRow
                label="Joined"
                value={format(new Date(submission.submittedAt), 'MMM dd, yyyy')}
              />
              <InfoRow label="Registration" value={submission.registrationNumber} />
            </div>
          </div>

          {/* Activity Timeline */}
          <div className="rounded-3xl bg-white shadow-soft p-6">
            <div className="flex items-center gap-2 mb-5 pb-4 border-b border-[var(--border-subtle)]">
              <Clock className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
              <span className="text-[17px] font-semibold text-[var(--text-primary)]">Activity Timeline</span>
            </div>

            <div className="space-y-0">
              <TimelineItem
                title="KYC submission received"
                time={formatDistanceToNow(new Date(submission.submittedAt), { addSuffix: true })}
                color="text-[var(--state-success)]"
              />
              <TimelineItem
                title="Documents uploaded"
                time={formatDistanceToNow(new Date(submission.submittedAt), { addSuffix: true })}
                color="text-[var(--text-accent)]"
              />
              <TimelineItem
                title="Merchant account created"
                time={formatDistanceToNow(
                  new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
                  { addSuffix: true }
                )}
                color="text-[var(--state-warning)]"
              />
              <TimelineItem
                title="Email verified"
                time={formatDistanceToNow(
                  new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
                  { addSuffix: true }
                )}
                color="text-[var(--state-success)]"
              />
              <TimelineItem
                title="Registration started"
                time={formatDistanceToNow(
                  new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
                  { addSuffix: true }
                )}
                color="text-[var(--text-muted)]"
                isLast
              />
            </div>
          </div>

          {/* Internal Notes */}
          <div className="rounded-3xl bg-white shadow-soft p-6">
            <div className="flex items-center gap-2 mb-5 pb-4 border-b border-[var(--border-subtle)]">
              <Edit3 className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
              <span className="text-[17px] font-semibold text-[var(--text-primary)]">Internal Notes</span>
            </div>

            <textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              placeholder="Add verification notes, concerns, or observations..."
              className="w-full min-h-[120px] rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors resize-vertical"
            />
            <button
              onClick={handleSaveNotes}
              className="w-full mt-3 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
            >
              Save Notes
            </button>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div
          className="fixed inset-0 bg-[rgba(15,61,30,0.45)] flex items-center justify-center z-50 p-4"
          onClick={() => setShowApproveModal(false)}
        >
          <div
            className="rounded-3xl bg-white p-8 shadow-lift max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6">
              <h2 className="text-[22px] font-semibold text-[var(--text-primary)] mb-2">
                Approve KYC Submission
              </h2>
              <p className="text-[15px] text-[var(--text-secondary)]">
                This will activate {submission.businessName}'s merchant account
              </p>
            </div>

            <form onSubmit={handleApprove}>
              <div className="mb-6">
                <label className="block text-[13px] font-semibold text-[var(--text-primary)] mb-2">
                  Approval Notes (Optional)
                </label>
                <textarea
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder="Add any notes about this approval..."
                  className="w-full min-h-[100px] rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors resize-vertical"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowApproveModal(false)}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-all duration-200 hover:border-[var(--border-strong)]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--gradient-to)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
                >
                  <CheckCircle className="w-4 h-4" strokeWidth={1.5} />
                  Confirm Approval
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div
          className="fixed inset-0 bg-[rgba(15,61,30,0.45)] flex items-center justify-center z-50 p-4"
          onClick={() => setShowRejectModal(false)}
        >
          <div
            className="rounded-3xl bg-white p-8 shadow-lift max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6">
              <h2 className="text-[22px] font-semibold text-[var(--text-primary)] mb-2">
                Reject KYC Submission
              </h2>
              <p className="text-[15px] text-[var(--text-secondary)]">
                Please provide a reason for rejection
              </p>
            </div>

            <form onSubmit={handleReject}>
              <div className="mb-4">
                <label className="block text-[13px] font-semibold text-[var(--text-primary)] mb-2">
                  Rejection Reason *
                </label>
                <select
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  required
                  className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                >
                  <option value="">Select a reason...</option>
                  <option value="incomplete">Incomplete documents</option>
                  <option value="invalid">Invalid or expired documents</option>
                  <option value="mismatch">Information mismatch</option>
                  <option value="fraud">Suspected fraud</option>
                  <option value="quality">Poor document quality</option>
                  <option value="other">Other reason</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-[13px] font-semibold text-[var(--text-primary)] mb-2">
                  Additional Details *
                </label>
                <textarea
                  value={rejectionDetails}
                  onChange={(e) => setRejectionDetails(e.target.value)}
                  required
                  placeholder="Provide specific details about why this KYC is being rejected..."
                  className="w-full min-h-[100px] rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors resize-vertical"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowRejectModal(false)}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-all duration-200 hover:border-[var(--border-strong)]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--state-danger)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(179,38,30,0.32)] active:brightness-95"
                >
                  <X className="w-4 h-4" strokeWidth={1.5} />
                  Confirm Rejection
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helper Components
// ---------------------------------------------------------------------------

const ChecklistItem = ({
  checked,
  onToggle,
  label,
  description,
}: {
  checked: boolean;
  onToggle: () => void;
  label: string;
  description: string;
}) => (
  <div
    className="flex items-center gap-3 py-3 border-b border-[var(--border-subtle)] last:border-0 cursor-pointer hover:bg-[var(--bg-base)] rounded-xl px-2 transition-colors"
    onClick={onToggle}
  >
    {checked ? (
      <CheckCircle className="w-5 h-5 flex-shrink-0 text-[var(--state-success)]" strokeWidth={1.5} />
    ) : (
      <div className="w-5 h-5 flex-shrink-0 rounded-full border-2 border-[var(--border-default)]" />
    )}
    <div className="flex-1 min-w-0">
      <div className="text-[14px] text-[var(--text-secondary)] font-medium">{label}</div>
      <div className="text-[12px] text-[var(--text-muted)] mt-0.5">{description}</div>
    </div>
  </div>
);

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <div className="py-3 border-b border-[var(--border-subtle)] last:border-0">
    <div className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">
      {label}
    </div>
    <div className="text-[14px] text-[var(--text-primary)] mt-0.5">{value}</div>
  </div>
);

const TimelineItem = ({
  title,
  time,
  color,
  isLast = false,
}: {
  title: string;
  time: string;
  color: string;
  isLast?: boolean;
}) => (
  <div className="flex gap-3 pb-4 relative">
    {!isLast && (
      <div className="absolute left-[0.6rem] top-6 bottom-0 w-px bg-[var(--bg-elevated)]" />
    )}
    <div
      className={`w-5 h-5 rounded-full bg-white border-2 border-current flex-shrink-0 relative z-10 mt-0.5 ${color}`}
    />
    <div className="flex-1 pt-0">
      <div className="text-[14px] text-[var(--text-secondary)]">{title}</div>
      <div className="text-[12px] text-[var(--text-muted)] mt-0.5">{time}</div>
    </div>
  </div>
);
